import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { Button, ButtonMode } from '@components/atoms/Button';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { InputErrors } from '@types';
import { useCreateWorker } from '@services/useCreateWorker';
import { useEditWorker } from '@services/useEditWorker';

interface WorkerFormErrors {
    general: string;
    name: InputErrors;
    state: InputErrors;
}

interface WorkerFormProps {
    edit: boolean;
    storeUuid: string;
}

export const WorkerForm: FC<WorkerFormProps> = ({ edit, storeUuid }) => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { login, myAccountWorkerDetail, isLoadingMyAccountWorkerDetail, createdWorker } = useData();
    const {
        request: requestCreateWorker,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successMessageCreate,
        lastResponseTime: lastCreate,
    } = useCreateWorker();
    const {
        request: requestEditWorker,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successMessageEdit,
        lastResponseTime: lastEdit,
    } = useEditWorker();
    const { t } = useTranslation();

    const translations = {
        nameLabel: t('manage-workers-form.worker.name.label'),
        stateLabel: t('manage-workers-form.worker.state.label'),
        nameRequiredError: t('manage-workers-form.worker.name.required-error'),
        stateRequiredError: t('manage-workers-form.worker.state.required-error'),
        submitButton: t('manage-workers-form.submit.button'),
        default: t('select-default-option'),
        workerStateActive: t('worker-state.active'),
        workerStateInactive: t('worker-state.inactive'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>('');
    const [state, setState] = useState<string | null>(null);

    // initially, update fields
    useEffect(() => {
        if (edit) {
            setName(myAccountWorkerDetail?.data?.name || '');
            setState(myAccountWorkerDetail?.data?.status || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountWorkerDetail]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<WorkerFormErrors>({
        general: '',
        name: [],
        state: [],
    });

    const handleOnClickSaveWorker = () => {
        const errors = validateForm();

        if (errors.name.length === 0 && errors.state.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => WorkerFormErrors = () => {
        const newErrors: WorkerFormErrors = {
            general: '',
            name: [],
            state: [],
        };

        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        !state && newErrors.state.push(translations.stateRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        if (edit === true) {
            const data = {
                name: name,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                status: (state || '') as any,
                store_uuid: myAccountWorkerDetail?.data?.store,
            };
            requestEditWorker({ uuid: myAccountWorkerDetail?.data?.uuid || '' }, data);
        } else {
            const data = {
                name: name,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                status: (state || '') as any,
                store_uuid: storeUuid,
            };
            requestCreateWorker(data);
        }
    };

    // if create service returned successfully, dispatch notification and go to worker's edit page
    // if not, show errors
    useEffect(() => {
        if (lastCreate) {
            if (errorMessageCreate || validationErrorsCreate) {
                setErrors({
                    general: errorMessageCreate as string,
                    // match form request errors with frontend errors object
                    name: validationErrorsCreate?.name || [],
                    state: validationErrorsCreate?.status || [],
                });
            } else if (login && createdWorker !== null) {
                showDefaultNotification({ type: 'success', message: successMessageCreate || '' });
                navigate(`/${SITE_URLS.myWorkers.replace(':store', `${storeUuid}`)}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreate, createdWorker]);

    // if edit service returned successfully, dispatch notification and update
    // if not, show error
    useEffect(() => {
        if (lastEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                setErrors({
                    general: errorMessageEdit as string,
                    // match form request errors with frontend errors object
                    name: validationErrorsEdit?.name || [],
                    state: validationErrorsEdit?.status || [],
                });
            } else if (login) {
                showDefaultNotification({ type: 'success', message: successMessageEdit || '' });
                navigate(`/${SITE_URLS.myWorkers.replace(':store', `${myAccountWorkerDetail?.data?.store}`)}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastEdit]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>

            <div className="row">
                {/* name */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.nameLabel}*`}
                        value={name}
                        onChange={(value) => setName(value)}
                        disabled={isLoadingMyAccountWorkerDetail}
                        state={errors.name.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.name}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* state */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.stateLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyAccountWorkerDetail}
                            options={[
                                { value: '', text: translations.default },
                                { value: 'active', text: translations.workerStateActive },
                                { value: 'inactive', text: translations.workerStateInactive },
                            ]}
                            value={state || undefined}
                            onChange={(value) => setState(value)}
                        />
                        {errors.state.length > 0 && <ErrorMessages messages={errors.state} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
            </div>

            {/* submit */}
            <div className="col-12 text-end">
                <Button
                    icon="fa-solid fa-floppy-disk"
                    label={translations.submitButton}
                    mode={ButtonMode.primary}
                    disabled={isLoadingMyAccountWorkerDetail}
                    onClick={() => handleOnClickSaveWorker()}
                />
            </div>
        </Form>
    );
};
